import {
  translate,
  formatCurrency,
  GetBackgroundStyle,
} from "@talladega/helpers";
import FinancingPaymentAmountContext, {
  FinancingPageState,
} from "@modules/purchase/consumer/ConsumerFinancingPageContext";
import { useTranslation, Trans } from "next-i18next";
import { useRouter } from "next/router";
import { useContext, useState } from "react";
import useConsumerFinancingQuery from "@modules/purchase/consumer/hooks/useConsumerFinancingFromQuery";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import {
  BubblesFinancingEn,
  BubblesFinancingFr,
  FinancingHeroEn,
  FinancingHeroFr,
} from "public/images/purchase/consumer";
import KijijiLogoWhite from "@public/images/kijiji-logo-white.svg";
import AutoCanLogoWhite from "@public/images/autocan-logo-white.svg";
import useConsumerFinancing from "@modules/purchase/consumer/hooks/useConsumerFinancing";
import { OverlayLoader } from "@talladega/components";
import useStartConsumerFinancingMutation from "@modules/purchase/consumer/hooks/useStartConsumerFinancingMutation";
import { FinancingPreQualificationFormLayout } from "@modules/cms/types/FinancingPreQualificationFormLayout";
import LocalImage from "@common/components/LocalImage";
import PurchaseSourceVehicleImage from "@common/components/PurchaseSourceVehicleImage";
import {
  FinancingPreQualificationMode,
  introductionMode,
} from "../types/FinancingPreQualificationMode";
import { ConsumerFinancing } from "../types/ConsumerFinancing";
import FinancingPreQualificationForm from "./FinancingPreQualificationForm";

type Props = {
  layout: FinancingPreQualificationFormLayout;
};

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: FinancingHeroEn,
  srcFr: FinancingHeroFr,
  altKey: "purchase:alt_c2c_vehicle_img",
};

const bubblesImage: ImageWithTranslation = {
  srcEn: BubblesFinancingEn,
  srcFr: BubblesFinancingFr,
  altKey: "purchase:alt_c2c_info_img",
};

const FinancingPreQualification = ({ layout }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale } = router;
  const { background } = layout;

  const { financingState } = useContext(FinancingPaymentAmountContext);

  const [state] = financingState ?? [{}];

  const [financingPreQualModeState, setFinancingPreQualModeState] =
    useState<FinancingPreQualificationMode>(introductionMode);

  const data: FinancingPageState = {
    paymentAmount: state.paymentAmount ?? 0,
    paymentFrequency: state.paymentFrequency ?? null,
  };

  const {
    consumerFinancing: consumerFinancingOrNotFound,
    isLoading: isConsumerFinancingLoading,
  } = useConsumerFinancing();
  const consumerFinancingQuery = useConsumerFinancingQuery();
  const consumerFinancing = consumerFinancingOrNotFound as ConsumerFinancing;
  const { year, make, model, coverImage } = consumerFinancingQuery;
  const vehicleCoverImage = coverImage || consumerFinancing?.vehicle?.image;
  const { isPending: isStartConsumerFinancingPending } =
    useStartConsumerFinancingMutation();

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <OverlayLoader
      isLoading={isConsumerFinancingLoading || isStartConsumerFinancingPending}
    >
      <div
        className="bg-white overflow-visible no-scrollbar relative bg-cover bg-center"
        style={GetBackgroundStyle(background, baseDomain)}
      >
        <div className="relative max-w-page mx-auto flex gap-10 md:gap-16 items-center md:items-start flex-col pt-10 pb-6 px-4 lg:px-5 lg:py-16 lg:flex-row">
          <div className="flex flex-col lg:basis-[45%]">
            <div className="flex-1 w-full relative">
              <div className="min-w-full h-auto relative inset-0">
                <div className="flex-1 lg:basis-[40%] lg:px-0">
                  <div className="w-full items-center justify-center lg:text-left lg:items-start lg:justify-start">
                    <div className="text-3xl md:text-5xl font-bold pb-5 text-white md:leading-[3.625rem]">
                      {year &&
                      make &&
                      model &&
                      data.paymentAmount !== 0 &&
                      data.paymentFrequency ? (
                        <Trans
                          t={t}
                          i18nKey="purchase:interested_in_financing_specific_vehicle"
                          values={{
                            vehicle: `${year} ${make} ${model}`,
                            payment: formatCurrency(
                              Math.round(data.paymentAmount),
                              locale || "en",
                              { showCents: "never" }
                            ),
                            frequency: translate(
                              data.paymentFrequency.name,
                              locale || "en"
                            ),
                          }}
                          components={{
                            bold: <span className="text-primary-bold" />,
                          }}
                        />
                      ) : (
                        <Trans
                          t={t}
                          i18nKey="purchase:interested_in_financing_a_vehicle"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-white mt-2 w-full items-center justify-center lg:text-left lg:items-start lg:justify-start">
                    <p className="text-gray-100 text-base font-medium md:text-lg">
                      <Trans
                        t={t}
                        i18nKey="purchase:purchasing_a_car_description"
                      />
                    </p>
                  </div>
                </div>
                {/** Brands Logo Section */}
                <div className="md:flex md:flex-wrap mt-4 lg:mb-[8px] pb-8">
                  <div className="float-left pt-1 md:pt-2 text-sm text-gray-50 sm:w-auto sm:mb-0 sm:mr-1">
                    {t("common:in_association_with")}
                  </div>

                  <div className="float-left flex flex-row justify-start items-center sm:w-auto">
                    <LocalImage
                      className="h-auto w-16 lg:w-[75px] flex-none mb-[1px] px-2 lg:mb-[-5px]"
                      src={KijijiLogoWhite}
                      alt="Kijiji Logo"
                      priority
                    />
                    <hr className="border-l border-gray-200 h-[25px]" />

                    <LocalImage
                      className="ml-3 h-auto w-28 lg:w-[120px] flex-none mb-[1px]"
                      src={AutoCanLogoWhite}
                      alt="Autocan Logo"
                      priority
                    />
                  </div>
                </div>
                {vehicleCoverImage ? (
                  <div>
                    <div className="absolute -right-4 md:-right-1 lg:-right-14 xl:-right-10 z-[2]">
                      <LocalImage
                        className="h-48 w-40 md:h-96 md:w-96 object-contain rounded-lg"
                        src={
                          locale === "fr"
                            ? bubblesImage.srcFr
                            : bubblesImage.srcEn
                        }
                        alt={t(bubblesImage.altKey)}
                      />
                    </div>

                    <div className="flex justify-center overflow-hidden relative md:h-full">
                      <div className="rounded-full border-white border-[.5rem] mr-9 md:mr-32 lg:mr-44 h-80 w-80 md:border-[1rem] md:h-[27rem] md:w-[27rem] lg:h-[30rem] lg:w-[30rem]">
                        <PurchaseSourceVehicleImage
                          className="rounded-full w-full h-full object-cover"
                          src={vehicleCoverImage}
                          alt={t(vehicleDefaultImage.altKey)}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative h-[216px] md:h-[322px] lg:mr-4 mt-4">
                    <LocalImage
                      className="z-[2] h-auto w-full rounded-lg object-contain"
                      src={
                        locale === "fr"
                          ? vehicleDefaultImage.srcFr
                          : vehicleDefaultImage.srcEn
                      }
                      alt={t(vehicleDefaultImage.altKey)}
                      fill
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="md:mt-0 bg-primary rounded-2xl p-6 md:p-10 flex-1 basis-[45%]">
            <FinancingPreQualificationForm
              modeState={[
                financingPreQualModeState,
                setFinancingPreQualModeState,
              ]}
            />
          </div>
        </div>
      </div>
    </OverlayLoader>
  );
};

export default FinancingPreQualification;
